<template>
  <div>
    <div class="py-8 bg-white rounded-lg bx-shadow mt-3">
      <div v-show="step === 1" class="flex justify-center flex-col items-center register" style="min-width: 200px">
        <h2 class="text-black text-center font-medium text-2xl register-title">Create Account</h2>

        <v-form @submit.prevent="register" ref="registerForm" class="register-form" style="width: 100%">
          <v-row>
            <!-- ── Personal Info ── -->
            <v-col cols="12" class="pb-1">
              <span class="section-label">Personal Info</span>
            </v-col>

            <v-col cols="12" lg="6" md="6" sm="6" xs="12" class="pb-0">
              <v-text-field
                :rules="[rules.required]"
                v-model="form.firstName"
                outlined
                clearable
                dense
                label="First Name"
                prepend-inner-icon="mdi-account-outline"
                hide-details
                color="orange darken-1" />
            </v-col>

            <v-col cols="12" lg="6" md="6" sm="6" xs="12" class="pb-0">
              <v-text-field
                v-model="form.lastName"
                outlined
                clearable
                dense
                label="Last Name"
                prepend-inner-icon="mdi-account-outline"
                hide-details
                color="orange darken-1" />
            </v-col>
            <v-col cols="12" lg="6" md="6" sm="6" xs="12" class="pb-0">
              <v-text-field
                :rules="[rules.required]"
                v-model="form.email"
                outlined
                clearable
                dense
                label="Email"
                prepend-inner-icon="mdi-email-outline"
                hide-details
                color="orange darken-1" />
            </v-col>

            <v-col cols="12" lg="6" md="6" sm="12" xs="12" class="pb-0">
              <VuePhoneNumberInput
                :default-country-code="selectedCountryCode"
                @update="logPhone"
                :error="!phoneValid"
                v-model="form.phone"
                :clearable="true"
                style="height: 48px !important"
                :countries-height="46" />
            </v-col>

            <!-- ── Password ── -->
            <v-col cols="12" class="pb-1 pt-5">
              <span class="section-label">Password</span>
            </v-col>

            <v-col cols="12" lg="4" md="12" sm="12" xs="12" class="pb-0">
              <v-btn color="primary" style="height: 40px; width: 100%" @click="uuidv4">Generate Password</v-btn>
            </v-col>

            <v-col cols="12" lg="4" md="6" sm="6" xs="12" class="pb-0" style="position: relative">
              <v-text-field
                :rules="[rules.required]"
                v-model="form.password"
                :append-icon="passEye ? 'mdi-eye-outline' : 'mdi-eye-off-outline'"
                outlined
                clearable
                dense
                label="Password"
                prepend-inner-icon="mdi-lock-outline"
                :type="passEye ? 'text' : 'password'"
                @click:append="passEye = !passEye"
                hide-details
                color="orange darken-1" />
              <password-control
                v-on:done="isDone = $event"
                :ifCondition="this.form.password && this.form.password !== ''"
                :password="this.form.password"
                :passwordConfirm="passwordConfirm" />
            </v-col>

            <v-col cols="12" lg="4" md="6" sm="6" xs="12" class="pb-0">
              <v-text-field
                :rules="[rules.required]"
                v-model="passwordConfirm"
                :append-icon="passEye ? 'mdi-eye-outline' : 'mdi-eye-off-outline'"
                @click:append="passEye = !passEye"
                hide-details
                dense
                :type="passEye ? 'text' : 'password'"
                outlined
                clearable
                label="Password Confirm"
                prepend-inner-icon="mdi-lock-check-outline"
                color="orange darken-1" />
            </v-col>

            <!-- ── Tenant & Language ── -->
            <v-col cols="12" class="pb-1 pt-5">
              <span class="section-label">Tenant & Language</span>
            </v-col>

            <v-col class="register-tenant pb-0" cols="12" lg="6" md="6" sm="6" xs="12">
              <SelectTenant
                :isAll="true"
                :tenants="allTenants"
                :allTenants="listedTenants"
                v-on:onSelect="selectTenant($event)" />
            </v-col>

            <v-col cols="12" lg="6" md="6" sm="6" xs="12" class="pb-0">
              <v-select
                v-model="selectedLanguage"
                :items="languages"
                item-text="text"
                item-value="value"
                label="Select Language"
                prepend-inner-icon="mdi-translate"
                outlined
                hide-details
                clearable
                color="orange darken-1">
                <template v-slot:append-outer>
                  <v-tooltip bottom color="grey darken-3">
                    <template v-slot:activator="{ on }">
                      <v-icon v-on="on">mdi-help-circle-outline</v-icon>
                    </template>
                    <span>The email will be sent in the selected language.</span>
                  </v-tooltip>
                </template>
              </v-select>
            </v-col>

            <!-- ── Info & Submit ── -->
            <v-col cols="12" class="pb-0 pt-0">
              <v-alert
                type="info"
                color="accent"
                dense
                text
                style="font-size: 13px; margin-bottom: 0; margin-top: -8px; border-radius: 8px">
                An email with password information will be sent to the account's email address.
              </v-alert>
            </v-col>

            <v-col cols="12" class="pt-4 d-flex justify-center">
              <v-btn
                @click="register"
                dark
                color="accent"
                elevation="1"
                style="height: 44px; width: 280px; font-size: 14px; border-radius: 8px; letter-spacing: 0.8px">
                <span v-show="!loading">
                  <v-icon left small>mdi-account-plus-outline</v-icon>
                  Create Account
                </span>
                <div v-show="loading" class="mng-loader"></div>
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </div>
    </div>
  </div>
</template>

<script>
import { getErrorMessage } from "@/util/helpers";
import { parsePhone } from "@/util/phone";
import Trainplanet from "@/util/trainplanet.api";
import passwordControl from "@/components/common/PasswordController";
import router from "@/router";
import store from "@/store";
import VuePhoneNumberInput from "vue-phone-number-input";
import "vue-phone-number-input/dist/vue-phone-number-input.css";
import { mapGetters } from "vuex";
import AuthApi from "../../../services/auth.api";
import SelectTenant from "../../../util/shared-components/select-tenant.vue";

export default {
  name: "AccountRegister",
  components: { SelectTenant, passwordControl, VuePhoneNumberInput },
  props: {
    preparedData: {
      type: Object,
      default: () => ({}),
    },
    isPopUp: { type: Boolean, default: false },
  },
  emits: ["registerDone"],
  data() {
    const prefilledPhone = this.preparedData.phone ?? "";
    const parsedPhone = parsePhone(prefilledPhone);
    return {
      selectedCountryCode: parsedPhone ? parsedPhone.countryCode : "SE",
      formattedPhone: parsedPhone ? parsedPhone.formatted : prefilledPhone,
      // An unparseable prefill blocks the register until it is fixed, so the raw value is
      // never submitted. No prefill at all stays valid.
      phoneValid: !prefilledPhone || !!parsedPhone,
      rules: {
        required: (value) => !!value || "Required.",
      },
      form: {
        lastName: this.preparedData.lastName ?? "",
        firstName: this.preparedData.firstName ?? "",
        phone: parsedPhone ? parsedPhone.nationalNumber : prefilledPhone,
        email: this.preparedData.email ?? "",
        password: "",
      },
      sharedItems: passwordControl.data,
      passwordConfirm: "",
      passEye: false,
      hasError: 0,
      loading: false,
      allTenants: [],
      successMsg: false,
      isDone: false,
      step: 1,
      tenantId: "",
      token: "",
      listedTenants: [],
      selectedLanguage: "sv-SE",
      languages: [],
    };
  },
  computed: {
    ...mapGetters({
      allTenantsMap: "auth/userTenants",
      userRoles: "auth/userRoles",
      user: "auth/user",
    }),
    AuthModel() {
      return new AuthApi(this.token, this.tenantId, this.selectedLanguage);
    },
  },
  watch: {
    allTenantsMap(value) {
      this.listedTenants = value;
    },
  },
  created() {
    this.getTenant();
    this.getToken();
    this.getLanguages();
  },
  methods: {
    async getLanguages() {
      try {
        const { locales } = await Trainplanet.getOrderSystemOptions();
        this.languages = Object.entries(locales).map(([value, text]) => ({ value, text }));
      } catch (error) {
        this.$store.dispatch("error", getErrorMessage(error));
      }
    },
    logPhone(payload) {
      this.selectedCountryCode = payload.countryCode;
      this.formattedPhone = payload.formatInternational || "";
      // The component emits `update` before `input`, so form.phone is still the previous
      // value here. payload.phoneNumber is the current one and is unset while empty.
      this.phoneValid = payload.phoneNumber ? payload.isValid : true;
    },
    uuidv4() {
      const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
      let result = Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
      if (!/[0-9]/.test(result)) return this.uuidv4();
      this.form.password = result;
      this.passwordConfirm = result;
      this.passEye = true;
    },
    selectTenant(event) {
      this.tenantId = typeof event === "object" ? event.id : event;
    },
    async getTenant() {
      const res = await this.$store.dispatch("auth/getUser");
      this.allTenants = res.tenants;
    },
    async getToken() {
      this.token = await store.dispatch("auth/acquireToken", router.history.current.meta.scopes);
    },
    goAuth() {
      this.$router.push({ name: "Login" });
    },
    register() {
      this.hasError = this.successMsg = false;
      if (!this.$refs.registerForm.validate() || !this.phoneValid) return;
      if (!this.isDone) {
        this.$store.dispatch("error", "Password is not correct type");
        return;
      }
      this.loading = true;
      const phoneString = this.form.phone ? this.formattedPhone : "";
      this.AuthModel.register(
        {
          phone: phoneString || null,
          email: this.form.email,
          firstName: this.form.firstName,
          lastName: this.form.lastName,
          password: this.form.password,
        },
        this.selectedLanguage
      )
        .then(({ data }) => {
          this.$store.dispatch("success", "Account registered successfully");
          if (!this.isPopUp) {
            this.$router.push({ name: "account-detail", params: { id: data.id } });
          } else {
            this.$emit("registerDone", {
              ...data,
              email: data.email ?? this.form.email,
              firstName: data.firstName ?? this.form.firstName,
              lastName: data.lastName ?? this.form.lastName,
              fullName: data.fullName ?? `${this.form.firstName} ${this.form.lastName}`.trim(),
            });
          }
        })
        .catch((error) => {
          this.hasError = error.response.data.status;
          this.$store.dispatch("error", getErrorMessage(error));
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
::v-deep .password-box img {
  width: 20px;
  height: 20px;
}

::v-deep .password-box .item {
  align-items: center;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 10px;
}

::v-deep .password-box .item:not(:first-child) {
  margin-top: 6px;
}

::v-deep .password-box {
  border: 1px solid rgba(0, 0, 0, 0.589);
}

::v-deep .password-box {
  padding: 1rem;
}

@media (min-width: 900px) {
  .register {
    padding: 0 80px;
  }
  .register-form {
    max-width: 1100px;
    margin: 0 auto;
    background: #ffffff;
    border-radius: 12px;
    padding: 40px 48px;
    box-shadow: 0 1px 4px rgba(16, 24, 40, 0.06);
  }
}

.register-title {
  margin-bottom: 1.5rem;
}

.section-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  color: #bdbdbd;
}
.register-tenant .v-text-field.v-text-field--enclosed .v-text-field__details {
  display: none;
}
</style>
